import { createQueue } from "@proj-airi/stream-kit";
import rehypeParse from "rehype-parse";
import rehypeStringify from "rehype-stringify";
import { unified } from "unified";
import { visit } from "unist-util-visit";
import { stepCountAtLeast } from "@xsai/shared-chat";
import { streamText } from "@xsai/stream-text";
import { errorMessageFrom } from "@moeru/std";
//#region src/messages/context-prompt.ts
/**
* Render runtime context modules into a compact, readable text block.
*
* Use when:
* - Composing chat prompts that need to attach side-channel runtime context
*   (e.g. game state, system status) to the latest user message.
*
* Expects:
* - A snapshot keyed by `contextId`. Only the per-message `text` field is
*   included; volatile metadata (random IDs, ms timestamps) is excluded so
*   the output stays deterministic and KV-cache-friendly.
*
* Returns:
* - Empty string when the snapshot is empty.
* - Otherwise a `[Context]` block with one bullet per module, e.g.
*   `[Context]\n- system:minecraft-integration: Bot is online ...`
*
* Why this shape (not XML):
* - Weak local models (8B/14B) tend to mirror conspicuous structured
*   wrappers (`<context>...</context>`) back into their replies, treating
*   them as data to be quoted. A flat bullet list looks like ordinary
*   narrative, which suppresses that mirroring tendency.
* - See: https://github.com/moeru-ai/airi/issues/1539
*/
function formatContextPromptText(contextsSnapshot) {
	const entries = Object.entries(contextsSnapshot);
	if (entries.length === 0) return "";
	const lines = entries.flatMap(([contextId, messages]) => messages.map((m) => `- ${contextId}: ${m.text}`));
	if (lines.length === 0) return "";
	return ["[Context]", ...lines].join("\n");
}
/**
* Builds a user-role context prompt message from active runtime context.
*
* Use when:
* - A caller needs the historical standalone context prompt shape.
*
* Expects:
* - Context messages have already been bucketed and cloned by the context registry.
*
* Returns:
* - `null` when no prompt text is available.
* - A user message carrying the rendered context text otherwise.
*/
function buildContextPromptMessage(contextsSnapshot) {
	const promptText = formatContextPromptText(contextsSnapshot);
	if (!promptText) return null;
	return {
		role: "user",
		content: [{
			type: "text",
			text: promptText
		}]
	};
}
//#endregion
//#region src/messages/datetime-prefix.ts
function padDatePart(value) {
	return value.toString().padStart(2, "0");
}
/**
* Formats a timestamp as `[YYYY-MM-DD HH:MM] ` in the user's local timezone.
*
* Use when:
* - Annotating user messages so the model has a concrete time anchor on
*   every turn — historic and current user turns use the same shape so that
*   prefix-cache stays valid when a "current" turn becomes "historic" on
*   the next send.
* - Not used for assistant messages — that caused the model to mirror the
*   prefix into its own output.
*
* Returns:
* - String including a trailing space, e.g. `"[2026-04-25 18:47] "`.
*
* Before:
* - createdAt = 1745570820000  (a Unix ms in Asia/Shanghai)
*
* After:
* - "[2026-04-25 18:47] "
*/
function formatTimePrefix(createdAt) {
	const date = new Date(createdAt);
	return `[${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())} ${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}] `;
}
//#endregion
//#region src/runtime/agent-hooks.ts
function createChatHooks() {
	const onBeforeMessageComposedHooks = [];
	const onAfterMessageComposedHooks = [];
	const onBeforeSendHooks = [];
	const onAfterSendHooks = [];
	const onTokenLiteralHooks = [];
	const onTokenSpecialHooks = [];
	const onStreamEndHooks = [];
	const onAssistantResponseEndHooks = [];
	const onAssistantMessageHooks = [];
	const onChatTurnCompleteHooks = [];
	function onBeforeMessageComposed(cb) {
		onBeforeMessageComposedHooks.push(cb);
		return () => {
			const index = onBeforeMessageComposedHooks.indexOf(cb);
			if (index >= 0) onBeforeMessageComposedHooks.splice(index, 1);
		};
	}
	function onAfterMessageComposed(cb) {
		onAfterMessageComposedHooks.push(cb);
		return () => {
			const index = onAfterMessageComposedHooks.indexOf(cb);
			if (index >= 0) onAfterMessageComposedHooks.splice(index, 1);
		};
	}
	function onBeforeSend(cb) {
		onBeforeSendHooks.push(cb);
		return () => {
			const index = onBeforeSendHooks.indexOf(cb);
			if (index >= 0) onBeforeSendHooks.splice(index, 1);
		};
	}
	function onAfterSend(cb) {
		onAfterSendHooks.push(cb);
		return () => {
			const index = onAfterSendHooks.indexOf(cb);
			if (index >= 0) onAfterSendHooks.splice(index, 1);
		};
	}
	function onTokenLiteral(cb) {
		onTokenLiteralHooks.push(cb);
		return () => {
			const index = onTokenLiteralHooks.indexOf(cb);
			if (index >= 0) onTokenLiteralHooks.splice(index, 1);
		};
	}
	function onTokenSpecial(cb) {
		onTokenSpecialHooks.push(cb);
		return () => {
			const index = onTokenSpecialHooks.indexOf(cb);
			if (index >= 0) onTokenSpecialHooks.splice(index, 1);
		};
	}
	function onStreamEnd(cb) {
		onStreamEndHooks.push(cb);
		return () => {
			const index = onStreamEndHooks.indexOf(cb);
			if (index >= 0) onStreamEndHooks.splice(index, 1);
		};
	}
	function onAssistantResponseEnd(cb) {
		onAssistantResponseEndHooks.push(cb);
		return () => {
			const index = onAssistantResponseEndHooks.indexOf(cb);
			if (index >= 0) onAssistantResponseEndHooks.splice(index, 1);
		};
	}
	function onAssistantMessage(cb) {
		onAssistantMessageHooks.push(cb);
		return () => {
			const index = onAssistantMessageHooks.indexOf(cb);
			if (index >= 0) onAssistantMessageHooks.splice(index, 1);
		};
	}
	function onChatTurnComplete(cb) {
		onChatTurnCompleteHooks.push(cb);
		return () => {
			const index = onChatTurnCompleteHooks.indexOf(cb);
			if (index >= 0) onChatTurnCompleteHooks.splice(index, 1);
		};
	}
	function clearHooks() {
		onBeforeMessageComposedHooks.length = 0;
		onAfterMessageComposedHooks.length = 0;
		onBeforeSendHooks.length = 0;
		onAfterSendHooks.length = 0;
		onTokenLiteralHooks.length = 0;
		onTokenSpecialHooks.length = 0;
		onStreamEndHooks.length = 0;
		onAssistantResponseEndHooks.length = 0;
		onAssistantMessageHooks.length = 0;
		onChatTurnCompleteHooks.length = 0;
	}
	async function emitBeforeMessageComposedHooks(message, context) {
		for (const hook of onBeforeMessageComposedHooks) await hook(message, context);
	}
	async function emitAfterMessageComposedHooks(message, context) {
		for (const hook of onAfterMessageComposedHooks) await hook(message, context);
	}
	async function emitBeforeSendHooks(message, context) {
		for (const hook of onBeforeSendHooks) await hook(message, context);
	}
	async function emitAfterSendHooks(message, context) {
		for (const hook of onAfterSendHooks) await hook(message, context);
	}
	async function emitTokenLiteralHooks(literal, context) {
		for (const hook of onTokenLiteralHooks) await hook(literal, context);
	}
	async function emitTokenSpecialHooks(special, context) {
		for (const hook of onTokenSpecialHooks) await hook(special, context);
	}
	async function emitStreamEndHooks(context) {
		for (const hook of onStreamEndHooks) await hook(context);
	}
	async function emitAssistantResponseEndHooks(message, context) {
		for (const hook of onAssistantResponseEndHooks) await hook(message, context);
	}
	async function emitAssistantMessageHooks(message, messageText, context) {
		for (const hook of onAssistantMessageHooks) await hook(message, messageText, context);
	}
	async function emitChatTurnCompleteHooks(chat, context) {
		for (const hook of onChatTurnCompleteHooks) await hook(chat, context);
	}
	return {
		onBeforeMessageComposed,
		onAfterMessageComposed,
		onBeforeSend,
		onAfterSend,
		onTokenLiteral,
		onTokenSpecial,
		onStreamEnd,
		onAssistantResponseEnd,
		onAssistantMessage,
		onChatTurnComplete,
		emitBeforeMessageComposedHooks,
		emitAfterMessageComposedHooks,
		emitBeforeSendHooks,
		emitAfterSendHooks,
		emitTokenLiteralHooks,
		emitTokenSpecialHooks,
		emitStreamEndHooks,
		emitAssistantResponseEndHooks,
		emitAssistantMessageHooks,
		emitChatTurnCompleteHooks,
		clearHooks
	};
}
//#endregion
//#region src/runtime/llm-marker-parser.ts
const TAG_OPEN = "<|";
const TAG_CLOSE = "|>";
const ESCAPED_TAG_OPEN = "<{'|'}";
const ESCAPED_TAG_CLOSE = "{'|'}>";
function createPushStream() {
	let closed = false;
	let controller = null;
	return {
		stream: new ReadableStream({
			start(ctrl) {
				controller = ctrl;
			},
			cancel() {
				closed = true;
			}
		}),
		write(value) {
			if (!controller || closed) return;
			controller.enqueue(value);
		},
		close() {
			if (!controller || closed) return;
			closed = true;
			controller.close();
		},
		error(err) {
			if (!controller || closed) return;
			closed = true;
			controller.error(err);
		}
	};
}
async function readStream(stream, handler) {
	const reader = stream.getReader();
	try {
		while (true) {
			const { value, done } = await reader.read();
			if (done) break;
			await handler(value);
		}
	} finally {
		reader.releaseLock();
	}
}
function createLlmMarkerParser(options) {
	const minLiteralEmitLength = Math.max(1, options?.minLiteralEmitLength ?? 1);
	const tailLength = Math.max(1, 5);
	let buffer = "";
	let inTag = false;
	return {
		async consume(textPart, onLiteral, onSpecial) {
			buffer += textPart;
			buffer = buffer.replaceAll(ESCAPED_TAG_OPEN, TAG_OPEN).replaceAll(ESCAPED_TAG_CLOSE, TAG_CLOSE);
			while (buffer.length > 0) if (!inTag) {
				const openTagIndex = buffer.indexOf(TAG_OPEN);
				if (openTagIndex < 0) {
					if (buffer.length - tailLength >= minLiteralEmitLength) {
						const emit = buffer.slice(0, -tailLength);
						buffer = buffer.slice(-tailLength);
						await onLiteral(emit);
					}
					break;
				}
				if (openTagIndex > 0) {
					const emit = buffer.slice(0, openTagIndex);
					buffer = buffer.slice(openTagIndex);
					await onLiteral(emit);
				}
				inTag = true;
			} else {
				const closeTagIndex = buffer.indexOf(TAG_CLOSE);
				if (closeTagIndex < 0) break;
				const emit = buffer.slice(0, closeTagIndex + 2);
				buffer = buffer.slice(closeTagIndex + 2);
				await onSpecial(emit);
				inTag = false;
			}
		},
		async end(onLiteral) {
			if (!inTag && buffer.length > 0) {
				await onLiteral(buffer);
				buffer = "";
			}
		}
	};
}
function createLlmMarkerStream(input, options) {
	const { stream, write, close, error } = createPushStream();
	const parser = createLlmMarkerParser(options);
	readStream(input, async (chunk) => {
		await parser.consume(chunk, async (literal) => {
			if (!literal) return;
			write({
				type: "literal",
				value: literal
			});
		}, async (special) => {
			write({
				type: "special",
				value: special
			});
		});
	}).then(async () => {
		await parser.end(async (literal) => {
			if (!literal) return;
			write({
				type: "literal",
				value: literal
			});
		});
		close();
	}).catch((err) => {
		error(err);
	});
	return stream;
}
/**
* Creates a streaming parser for LLM responses with AIRI special markers.
*
* Use when:
* - Handling streamed model output that may contain `<|...|>` markers.
* - Literal text and special marker tokens need to be emitted separately.
*
* Expects:
* - Callers feed chunks in order and call `end()` once the model stream ends.
*
* Returns:
* - A parser with `consume()` and `end()` methods.
*/
function useLlmmarkerParser(options) {
	let fullText = "";
	const { stream, write, close } = createPushStream();
	const processing = readStream(createLlmMarkerStream(stream, { minLiteralEmitLength: options.minLiteralEmitLength }), async (token) => {
		if (token.type === "literal") await options.onLiteral?.(token.value);
		if (token.type === "special") await options.onSpecial?.(token.value);
	});
	return {
		async consume(textPart) {
			fullText += textPart;
			write(textPart);
		},
		async end() {
			close();
			await processing;
			await options.onEnd?.(fullText);
		}
	};
}
//#endregion
//#region src/runtime/response-categoriser.ts
/**
* Maps tag names to categories
* All tags are treated as reasoning (filtered from TTS)
*/
function mapTagNameToCategory(_tagName) {
	return "reasoning";
}
/**
* Extracts all XML-like tags from a response using rehype pipeline
* Works with any tag format: <tag>content</tag>
* Only extracts tags that are actually complete (have closing tags in source)
*/
function extractAllTags(response) {
	const tags = [];
	try {
		visit(unified().use(rehypeParse, { fragment: true }).parse(response), "element", (node) => {
			const position = node.position;
			if (!position?.start || !position?.end) return;
			const startIndex = getOffsetFromPosition(response, position.start);
			const endIndex = getOffsetFromPosition(response, position.end);
			if (startIndex === -1 || endIndex === -1) return;
			const fullMatch = response.slice(startIndex, endIndex);
			const expectedClosingTag = `</${node.tagName}>`;
			if (!fullMatch.includes(expectedClosingTag)) return;
			tags.push({
				tagName: node.tagName,
				content: extractTextContent(node),
				fullMatch,
				startIndex,
				endIndex
			});
		});
	} catch (error) {
		console.error("Failed to parse response for tag extraction:", error);
	}
	return tags;
}
/**
* Converts a position (line/column) to a character offset in the string
*/
function getOffsetFromPosition(text, position) {
	if (!position || typeof position.line !== "number" || typeof position.column !== "number") return -1;
	const lines = text.split("\n");
	let offset = 0;
	for (let i = 0; i < position.line - 1 && i < lines.length; i++) offset += lines[i].length + 1;
	offset += position.column - 1;
	return offset;
}
/**
* Extracts text content from an element node
*/
function extractTextContent(node) {
	const textParts = [];
	if (node.children) {
		for (const child of node.children) if (child.type === "text") textParts.push(child.value);
		else if (child.type === "element") textParts.push(extractTextContent(child));
	}
	return textParts.join("");
}
/**
* Categorizes a model response by dynamically extracting any XML-like tags
* Works with any tag format the model uses
*/
function categorizeResponse(response, _providerId) {
	const extractedTags = extractAllTags(response);
	if (extractedTags.length === 0) return {
		segments: [],
		speech: response,
		reasoning: "",
		raw: response
	};
	const segments = extractedTags.map((tag) => ({
		category: mapTagNameToCategory(tag.tagName),
		content: tag.content.trim(),
		startIndex: tag.startIndex,
		endIndex: tag.endIndex,
		raw: tag.fullMatch,
		tagName: tag.tagName
	}));
	segments.sort((a, b) => a.startIndex - b.startIndex);
	const speechParts = [];
	let lastEnd = 0;
	for (const segment of segments) {
		if (segment.startIndex > lastEnd) {
			const text = response.slice(lastEnd, segment.startIndex).trim();
			if (text) speechParts.push(text);
		}
		lastEnd = Math.max(lastEnd, segment.endIndex);
	}
	if (lastEnd < response.length) {
		const text = response.slice(lastEnd).trim();
		if (text) speechParts.push(text);
	}
	const reasoning = segments.filter((s) => s.category === "reasoning").map((s) => s.content).join("\n\n");
	return {
		segments,
		speech: speechParts.join(" ").trim() || "",
		reasoning,
		raw: response
	};
}
/**
* Note: This receives literal text from useLlmmarkerParser (special tokens <|...|> are already extracted).
* Only XML/HTML tags like <think>, <reasoning> need to be parsed here.
*/
function createStreamingCategorizer(providerId, onSegment) {
	let buffer = "";
	let categorized = null;
	let lastEmittedSegmentIndex = -1;
	let lastParsedLength = 0;
	let tagState = "outside";
	let tagStackDepth = 0;
	function checkIncompleteTag() {
		try {
			const tree = unified().use(rehypeParse, { fragment: true }).parse(buffer);
			const stringified = unified().use(rehypeStringify).stringify(tree).toString();
			if (stringified !== buffer) return buffer.trim().slice(-30) !== stringified.trim().slice(-30);
			return false;
		} catch {
			return true;
		}
	}
	function processChunkIncrementally(chunk) {
		let tagJustClosed = false;
		for (let i = 0; i < chunk.length; i++) {
			const char = chunk[i];
			switch (tagState) {
				case "outside":
					if (char === "<") if (i + 1 < chunk.length && chunk[i + 1] === "/") {
						tagState = "in-closing-tag";
						i++;
					} else tagState = "in-opening-tag";
					break;
				case "in-opening-tag":
					if (char === ">") {
						tagState = "in-content";
						tagStackDepth++;
					}
					break;
				case "in-content":
					if (char === "<") if (i + 1 < chunk.length && chunk[i + 1] === "/") {
						tagState = "in-closing-tag";
						i++;
					} else tagState = "in-opening-tag";
					break;
				case "in-closing-tag":
					if (char === ">") {
						tagStackDepth--;
						if (tagStackDepth === 0) {
							tagState = "outside";
							tagJustClosed = true;
						} else tagState = "in-content";
					}
					break;
			}
		}
		return tagJustClosed;
	}
	return {
		consume(chunk) {
			const tagJustClosed = processChunkIncrementally(chunk);
			buffer += chunk;
			if (!categorized || tagJustClosed || buffer.length - lastParsedLength > 1e3) {
				categorized = categorizeResponse(buffer, providerId);
				lastParsedLength = buffer.length;
			}
			if (!categorized) {
				categorized = categorizeResponse(buffer, providerId);
				lastParsedLength = buffer.length;
			}
			if (onSegment && categorized.segments.length > 0) for (let i = lastEmittedSegmentIndex + 1; i < categorized.segments.length; i++) {
				const segment = categorized.segments[i];
				if (buffer.length >= segment.endIndex) {
					onSegment(segment);
					lastEmittedSegmentIndex = i;
				}
			}
		},
		isSpeechAt(position) {
			if (!categorized || categorized.segments.length === 0) return true;
			for (const segment of categorized.segments) if (position >= segment.startIndex && position < segment.endIndex) return false;
			return true;
		},
		filterToSpeech(text, startPosition) {
			if (checkIncompleteTag()) {
				const fullText = buffer + text;
				try {
					const tree = unified().use(rehypeParse, { fragment: true }).parse(fullText);
					let closingOffset = -1;
					visit(tree, "element", (node) => {
						const position = node.position;
						if (position?.end && closingOffset === -1) {
							const endOffset = getOffsetFromPosition(fullText, position.end);
							const elementSource = fullText.slice(getOffsetFromPosition(fullText, position.start), endOffset);
							const expectedClosingTag = `</${node.tagName}>`;
							if (elementSource.includes(expectedClosingTag)) {
								if (endOffset >= buffer.length && endOffset <= fullText.length) closingOffset = endOffset - buffer.length;
							}
						}
					});
					if (closingOffset === -1) return "";
					text = text.slice(closingOffset);
					startPosition += closingOffset;
					categorized = categorizeResponse(buffer, providerId);
				} catch {
					return "";
				}
			}
			if (!categorized || categorized.segments.length === 0) return text;
			let filtered = "";
			const endPosition = startPosition + text.length;
			const overlappingSegments = categorized.segments.filter((segment) => segment.endIndex > startPosition && segment.startIndex < endPosition);
			if (overlappingSegments.length === 0) return text;
			let currentPos = startPosition;
			for (const segment of overlappingSegments) {
				const segmentStart = Math.max(segment.startIndex, startPosition);
				const segmentEnd = Math.min(segment.endIndex, endPosition);
				if (segmentStart > currentPos) {
					const beforeStart = currentPos - startPosition;
					const beforeEnd = segmentStart - startPosition;
					filtered += text.slice(beforeStart, beforeEnd);
				}
				currentPos = segmentEnd;
			}
			if (currentPos < endPosition) {
				const afterStart = currentPos - startPosition;
				filtered += text.slice(afterStart);
			}
			return filtered;
		},
		getCurrentPosition() {
			return buffer.length;
		},
		end() {
			return categorizeResponse(buffer, providerId);
		},
		getCurrent() {
			return categorized;
		}
	};
}
//#endregion
//#region src/runtime/chat-orchestrator-runtime.ts
const STREAMING_UI_FLUSH_CHUNK_SIZE = 24;
function prependTextToContent(msg, text) {
	const content = msg.content;
	if (content === void 0) return {
		...msg,
		content: text
	};
	if (typeof content === "string") return {
		...msg,
		content: `${text}${content}`
	};
	if (Array.isArray(content)) {
		const first = content[0];
		if (first && first.type === "text" && typeof first.text === "string") {
			const next = [{
				...first,
				text: `${text}${first.text}`
			}, ...content.slice(1)];
			return {
				...msg,
				content: next
			};
		}
		return {
			...msg,
			content: [{
				type: "text",
				text
			}, ...content]
		};
	}
	return msg;
}
function cloneStreamingMessage(message) {
	try {
		return structuredClone(message);
	} catch {
		return JSON.parse(JSON.stringify(message));
	}
}
function defaultCreateId() {
	return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
/**
* Creates the core chat orchestrator runtime used behind UI facades.
*
* Use when:
* - A platform wants AIRI chat send orchestration without Vue/Pinia coupling.
* - Session, context, foreground stream, and LLM integrations are provided as adapters.
*
* Expects:
* - Session messages are returned in chronological order.
* - `foregroundStream.patch` replaces the visible streaming assistant message.
*
* Returns:
* - A runtime with send queue APIs, hook registry, writable sending state, and queue snapshots.
*/
function createChatOrchestratorRuntime(deps) {
	const hooks = createChatHooks();
	const now = deps.now ?? (() => Date.now());
	const monotonicNow = deps.monotonicNow ?? (() => globalThis.performance?.now?.() ?? Date.now());
	const createId = deps.createId ?? defaultCreateId;
	const unwrapMessage = deps.unwrapMessage ?? ((message) => message);
	let sending = false;
	let pendingQueuedSends = [];
	function emitStateChange() {
		deps.onStateChange?.({
			sending,
			pendingQueuedSendCount: pendingQueuedSends.length
		});
	}
	function setSending(next) {
		if (sending === next) return;
		sending = next;
		emitStateChange();
	}
	function isForegroundSession(sessionId) {
		return sessionId === deps.getActiveSessionId();
	}
	function patchForegroundStream(sessionId, message) {
		if (isForegroundSession(sessionId)) deps.foregroundStream.patch(cloneStreamingMessage(message));
	}
	function resetForegroundStream(sessionId) {
		if (isForegroundSession(sessionId)) deps.foregroundStream.reset();
	}
	function ingestRuntimeContexts() {
		for (const provider of deps.runtimeContextProviders ?? []) {
			const contextMessage = provider();
			if (contextMessage) deps.context.ingest(contextMessage);
		}
	}
	function buildProviderMessages(sessionMessagesForSend) {
		const nowTs = now();
		return sessionMessagesForSend.map((msg) => {
			const { context: _context, id: _id, createdAt, ...withoutContext } = msg;
			const rawMessage = unwrapMessage(withoutContext);
			if (rawMessage.role === "user") return prependTextToContent(rawMessage, formatTimePrefix(createdAt ?? nowTs));
			if (rawMessage.role === "assistant") {
				const { slices: _slices, tool_results: _toolResults, categorization: _categorization, ...rest } = rawMessage;
				return unwrapMessage(rest);
			}
			return rawMessage;
		});
	}
	async function performSend(sendingMessage, options, generation, sessionId) {
		if (!sendingMessage && !options.attachments?.length) return;
		deps.session.ensureSession(sessionId);
		ingestRuntimeContexts();
		const sendingCreatedAt = now();
		const streamingMessageContext = {
			message: {
				role: "user",
				content: sendingMessage,
				createdAt: sendingCreatedAt,
				id: createId()
			},
			contexts: deps.context.snapshot(),
			composedMessage: [],
			input: options.input
		};
		deps.onLifecycle?.({
			phase: "before-compose",
			channel: "chat",
			sessionId,
			textPreview: sendingMessage,
			details: { contexts: streamingMessageContext.contexts }
		});
		const isStaleGeneration = () => deps.session.getSessionGeneration(sessionId) !== generation;
		const shouldAbort = () => isStaleGeneration();
		if (shouldAbort()) return;
		setSending(true);
		const buildingMessage = {
			role: "assistant",
			content: "",
			slices: [],
			tool_results: [],
			createdAt: now(),
			id: createId()
		};
		patchForegroundStream(sessionId, buildingMessage);
		deps.onTrackFirstMessage?.();
		deps.onMessageSendStarted?.({
			source: options.input ? "voice" : "text",
			model: options.model
		});
		const roundStartedAt = monotonicNow();
		try {
			await hooks.emitBeforeMessageComposedHooks(sendingMessage, streamingMessageContext);
			const contentParts = [{
				type: "text",
				text: sendingMessage
			}];
			if (options.attachments) {
				for (const attachment of options.attachments) if (attachment.type === "image") contentParts.push({
					type: "image_url",
					image_url: { url: `data:${attachment.mimeType};base64,${attachment.data}` }
				});
			}
			const finalContent = contentParts.length > 1 ? contentParts : sendingMessage;
			if (!streamingMessageContext.input) streamingMessageContext.input = {
				type: "input:text",
				data: { text: sendingMessage }
			};
			if (shouldAbort()) return;
			const userMessage = {
				role: "user",
				content: finalContent,
				createdAt: sendingCreatedAt,
				id: createId()
			};
			deps.session.appendSessionMessage(sessionId, userMessage);
			deps.onUserMessageAppended?.({
				sessionId,
				message: userMessage,
				messageText: sendingMessage
			});
			const sessionMessagesForSend = deps.session.getSessionMessages(sessionId);
			deps.onUserTurnReady?.({
				messageText: sendingMessage,
				sessionMessages: sessionMessagesForSend
			});
			const categorizer = createStreamingCategorizer(deps.getActiveProvider());
			let streamPosition = 0;
			const parser = useLlmmarkerParser({
				onLiteral: async (literal) => {
					if (shouldAbort()) return;
					categorizer.consume(literal);
					const speechOnly = categorizer.filterToSpeech(literal, streamPosition);
					streamPosition += literal.length;
					if (speechOnly.trim()) {
						buildingMessage.content += speechOnly;
						await hooks.emitTokenLiteralHooks(speechOnly, streamingMessageContext);
						const lastSlice = buildingMessage.slices.at(-1);
						if (lastSlice?.type === "text") lastSlice.text += speechOnly;
						else buildingMessage.slices.push({
							type: "text",
							text: speechOnly
						});
						patchForegroundStream(sessionId, buildingMessage);
					}
				},
				onSpecial: async (special) => {
					if (shouldAbort()) return;
					await hooks.emitTokenSpecialHooks(special, streamingMessageContext);
				},
				onEnd: async (fullText) => {
					if (isStaleGeneration()) return;
					const finalCategorization = categorizeResponse(fullText, deps.getActiveProvider());
					const reasoningContentField = buildingMessage.categorization?.reasoning?.trim();
					buildingMessage.categorization = {
						speech: finalCategorization.speech,
						reasoning: reasoningContentField || finalCategorization.reasoning
					};
					patchForegroundStream(sessionId, buildingMessage);
				},
				minLiteralEmitLength: STREAMING_UI_FLUSH_CHUNK_SIZE
			});
			const toolCallQueue = createQueue({ handlers: [async (ctx) => {
				if (shouldAbort()) return;
				if (ctx.data.type === "tool-call") {
					buildingMessage.slices.push(ctx.data);
					patchForegroundStream(sessionId, buildingMessage);
					return;
				}
				if (ctx.data.type === "tool-call-result") {
					buildingMessage.tool_results.push(ctx.data);
					patchForegroundStream(sessionId, buildingMessage);
				}
			}] });
			const newMessages = buildProviderMessages(sessionMessagesForSend);
			const systemPromptSupplement = deps.getSystemPromptSupplement?.()?.trim();
			if (systemPromptSupplement) {
				const systemMessage = newMessages.find((message) => message.role === "system");
				if (systemMessage) systemMessage.content = `${systemMessage.content}\n\n${systemPromptSupplement}`;
				else newMessages.unshift({
					role: "system",
					content: systemPromptSupplement
				});
			}
			const contextsSnapshot = deps.context.snapshot();
			const contextPromptText = formatContextPromptText(contextsSnapshot);
			if (contextPromptText) {
				const lastMessage = newMessages.at(-1);
				if (lastMessage && lastMessage.role === "user") lastMessage.content = [...typeof lastMessage.content === "string" ? [{
					type: "text",
					text: lastMessage.content
				}] : lastMessage.content, {
					type: "text",
					text: `\n${contextPromptText}`
				}];
				deps.onLifecycle?.({
					phase: "prompt-context-built",
					channel: "chat",
					sessionId,
					details: {
						contexts: contextsSnapshot,
						promptText: contextPromptText
					}
				});
			}
			streamingMessageContext.composedMessage = newMessages;
			deps.onPromptProjection?.({
				sessionId,
				message: sendingMessage,
				contexts: contextsSnapshot,
				promptMessage: void 0,
				composedMessage: newMessages
			});
			deps.onLifecycle?.({
				phase: "after-compose",
				channel: "chat",
				sessionId,
				textPreview: sendingMessage,
				details: { composedMessage: newMessages }
			});
			await hooks.emitAfterMessageComposedHooks(sendingMessage, streamingMessageContext);
			await hooks.emitBeforeSendHooks(sendingMessage, streamingMessageContext);
			let fullText = "";
			const headers = options.providerConfig?.headers || {};
			if (shouldAbort()) return;
			const llmRequestStartedAt = monotonicNow();
			let llmFirstTokenEmitted = false;
			deps.onLlmRequestStarted?.({
				model: options.model,
				provider: deps.getActiveProvider() || "unknown",
				hasVoice: !!options.input
			});
			await deps.llm.stream(options.model, options.chatProvider, newMessages, {
				headers,
				tools: options.tools,
				waitForTools: true,
				captureToolErrors: true,
				onStreamEvent: async (event) => {
					switch (event.type) {
						case "tool-call":
							toolCallQueue.enqueue({
								type: "tool-call",
								toolCall: event
							});
							break;
						case "tool-result":
							toolCallQueue.enqueue({
								type: "tool-call-result",
								id: event.toolCallId,
								result: event.result
							});
							break;
						case "tool-error":
							toolCallQueue.enqueue({
								type: "tool-call-result",
								id: event.toolCallId,
								isError: true,
								result: event.result
							});
							break;
						case "text-delta":
							if (!llmFirstTokenEmitted) {
								llmFirstTokenEmitted = true;
								deps.onLlmFirstToken?.({
									model: options.model,
									ttfbMs: Math.round(monotonicNow() - llmRequestStartedAt)
								});
							}
							fullText += event.text;
							await parser.consume(event.text);
							break;
						case "reasoning-delta": {
							if (shouldAbort()) return;
							const { reasoning = "" } = buildingMessage.categorization ?? {};
							const nextReasoning = reasoning + event.text;
							buildingMessage.categorization = {
								speech: typeof buildingMessage.content === "string" ? buildingMessage.content : "",
								reasoning: nextReasoning
							};
							const crossesBoundary = Math.floor(nextReasoning.length / STREAMING_UI_FLUSH_CHUNK_SIZE) > Math.floor(reasoning.length / STREAMING_UI_FLUSH_CHUNK_SIZE);
							if (!reasoning || crossesBoundary) patchForegroundStream(sessionId, buildingMessage);
							break;
						}
						case "finish": break;
						case "error": throw event.error ?? /* @__PURE__ */ new Error("Stream error");
					}
				}
			});
			await parser.end();
			deps.onAssistantResponseRendered?.({
				model: options.model,
				latencyMs: Math.round(monotonicNow() - llmRequestStartedAt)
			});
			if (!isStaleGeneration() && buildingMessage.slices.length > 0) {
				const finalAssistant = buildingMessage;
				deps.session.appendSessionMessage(sessionId, finalAssistant);
				deps.onAssistantMessageAppended?.({
					sessionId,
					message: finalAssistant,
					messageText: fullText
				});
			}
			await hooks.emitStreamEndHooks(streamingMessageContext);
			await hooks.emitAssistantResponseEndHooks(fullText, streamingMessageContext);
			await hooks.emitAfterSendHooks(sendingMessage, streamingMessageContext);
			await hooks.emitAssistantMessageHooks({ ...buildingMessage }, fullText, streamingMessageContext);
			await hooks.emitChatTurnCompleteHooks({
				output: { ...buildingMessage },
				outputText: fullText,
				toolCalls: sessionMessagesForSend.filter((msg) => msg.role === "tool")
			}, streamingMessageContext);
			deps.onAssistantTurnReady?.({
				messageText: fullText,
				sessionMessages: sessionMessagesForSend
			});
			resetForegroundStream(sessionId);
			deps.onMessageRound?.({
				durationMs: Math.round(monotonicNow() - roundStartedAt),
				hasVoice: !!options.input,
				model: options.model
			});
		} catch (error) {
			console.error("Error sending message:", error);
			throw error;
		} finally {
			setSending(false);
			deps.onSendSettled?.({ sessionId });
		}
	}
	const sendQueue = createQueue({ handlers: [async ({ data }) => {
		const { sendingMessage, options, generation, deferred, sessionId, cancelled } = data;
		if (cancelled) return;
		if (deps.session.getSessionGeneration(sessionId) !== generation) {
			deferred.reject(/* @__PURE__ */ new Error("Chat session was reset before send could start"));
			return;
		}
		try {
			await performSend(sendingMessage, options, generation, sessionId);
			deferred.resolve();
		} catch (error) {
			deferred.reject(error);
		}
	}] });
	sendQueue.on("enqueue", (queuedSend) => {
		pendingQueuedSends.push(queuedSend);
		emitStateChange();
	});
	sendQueue.on("dequeue", (queuedSend) => {
		pendingQueuedSends = pendingQueuedSends.filter((item) => item !== queuedSend);
		emitStateChange();
	});
	function ingest(sendingMessage, options, targetSessionId) {
		const sessionId = targetSessionId || deps.getActiveSessionId();
		const generation = deps.session.getSessionGeneration(sessionId);
		return new Promise((resolve, reject) => {
			sendQueue.enqueue({
				sendingMessage,
				options,
				generation,
				sessionId,
				deferred: {
					resolve,
					reject
				}
			});
		});
	}
	function cancelPendingSends(sessionId) {
		for (const queued of pendingQueuedSends) {
			if (sessionId && queued.sessionId !== sessionId) continue;
			queued.cancelled = true;
			queued.deferred.reject(/* @__PURE__ */ new Error("Chat session was reset before send could start"));
		}
		pendingQueuedSends = sessionId ? pendingQueuedSends.filter((item) => item.sessionId !== sessionId) : [];
		emitStateChange();
	}
	function getPendingQueuedSendSnapshot() {
		return pendingQueuedSends.map((queued) => ({
			sessionId: queued.sessionId,
			generation: queued.generation,
			cancelled: !!queued.cancelled,
			messagePreview: queued.sendingMessage.slice(0, 120),
			hasAttachments: !!queued.options.attachments?.length,
			inputType: queued.options.input?.type
		}));
	}
	return {
		ingest,
		cancelPendingSends,
		getPendingQueuedSendSnapshot,
		getPendingQueuedSendCount: () => pendingQueuedSends.length,
		getSending: () => sending,
		setSending,
		hooks
	};
}
//#endregion
//#region src/runtime/context-registry.ts
const CONTEXT_UPDATE_REPLACE_SELF = "replace-self";
const CONTEXT_UPDATE_APPEND_SELF = "append-self";
function formatMetadataSource(source) {
	if (!source) return void 0;
	if ("extension" in source) return `${source.extension.id}:${source.id}`;
	return source.id;
}
function defaultGetSourceKey(event, fallback = "unknown") {
	return formatMetadataSource(event.metadata?.source) ?? event.source ?? fallback;
}
/**
* Creates a context registry that owns active buckets and bounded ingest history.
*
* Use when:
* - Runtime contexts need replace-self or append-self bucket semantics.
* - UI or transport layers need cloned snapshots without owning mutation policy.
*
* Expects:
* - Context messages are structured-cloneable before they enter the registry.
* - Unknown strategies should still be recorded in history for observability.
*
* Returns:
* - A registry whose snapshots cannot mutate internal active bucket state.
*/
function createContextRegistry(options = {}) {
	const historyLimit = options.historyLimit ?? 400;
	const getSourceKey = options.getSourceKey ?? defaultGetSourceKey;
	let currentActiveContexts = /* @__PURE__ */ new Map();
	let currentContextHistory = [];
	function ingest(envelope) {
		const sourceKey = getSourceKey(envelope);
		const safeEnvelopeToStore = structuredClone(envelope);
		if (!currentActiveContexts.has(sourceKey)) currentActiveContexts.set(sourceKey, []);
		let result;
		if (envelope.strategy === CONTEXT_UPDATE_REPLACE_SELF) {
			currentActiveContexts.set(sourceKey, [safeEnvelopeToStore]);
			result = {
				sourceKey,
				mutation: "replace",
				entryCount: currentActiveContexts.get(sourceKey)?.length ?? 0
			};
		} else if (envelope.strategy === CONTEXT_UPDATE_APPEND_SELF) {
			currentActiveContexts.get(sourceKey)?.push(safeEnvelopeToStore);
			result = {
				sourceKey,
				mutation: "append",
				entryCount: currentActiveContexts.get(sourceKey)?.length ?? 0
			};
		}
		currentContextHistory = [...currentContextHistory, {
			...safeEnvelopeToStore,
			sourceKey
		}].slice(-historyLimit);
		return result;
	}
	function reset() {
		currentActiveContexts = /* @__PURE__ */ new Map();
		currentContextHistory = [];
	}
	function snapshot() {
		return Object.fromEntries(Array.from(currentActiveContexts, ([sourceKey, messages]) => [sourceKey, structuredClone(messages)]));
	}
	return {
		ingest,
		reset,
		snapshot,
		activeContexts: snapshot,
		contextHistory: () => structuredClone(currentContextHistory)
	};
}
//#endregion
//#region src/utils/error-message.ts
/**
* Returns an error message while preserving JavaScript string fallback.
*
* Use when:
* - Agent runtime diagnostics need a message for arbitrary thrown values.
*
* Expects:
* - `error` may be any thrown value.
*
* Returns:
* - The extracted error message, else `String(error)`.
*/
function errorMessageFromValue(error) {
	return errorMessageFrom(error) ?? String(error);
}
//#endregion
//#region src/runtime/llm-service.ts
/**
* Normalize chat messages so they match the wire format the active provider
* actually accepts, flattening content-part arrays back to plain strings when
* the provider can't deserialize arrays.
*
* Use when:
* - Composing the final message list right before handing it to the OpenAI-
*   compatible chat SDK.
*
* Expects:
* - `role: 'error'` entries (AIRI-internal markers from the chat UI). They are
*   rewritten as user-role narrations so the provider doesn't reject them.
* - `content` may be a string, a content-part array, or undefined.
*
* Returns:
* - A new array of `Message` values; original objects are not mutated.
*
* @param messages - Raw messages from the chat session, may include AIRI's
*   `error` role.
* @param supportsContentArray - When `false`, force-flatten every array
*   content (including text + `image_url` mixes) to a text-only string and
*   drop non-text parts. Drives the runtime auto-degrade for strict providers.
*   Defaults to `true` to preserve vision/multimodal payloads on capable
*   providers.
*/
function sanitizeMessages(messages, supportsContentArray = true) {
	return messages.map((message) => {
		if (message && message.role === "error") return {
			role: "user",
			content: `User encountered error: ${String(message.content ?? "")}`
		};
		if (message && Array.isArray(message.content)) {
			const contentParts = message.content;
			const hasNonTextPart = contentParts.some((part) => part?.type && part.type !== "text");
			if (!supportsContentArray || !hasNonTextPart) return {
				...message,
				content: contentParts.map((part) => part?.text ?? "").join("")
			};
		}
		return message;
	});
}
function modelKey(model, chatProvider) {
	return `${chatProvider.chat(model).baseURL}-${model}`;
}
function streamOptionsToolsCompatibilityOk(model, chatProvider, options) {
	if (options?.supportsTools !== void 0) return options.supportsTools;
	const key = modelKey(model, chatProvider);
	return options?.toolsCompatibility?.get(key) !== false;
}
/**
* Resolve whether the active model+provider currently supports content-part
* arrays. Defaults to `true` so first-time calls keep multimodal payloads;
* flips to `false` once {@link isContentArrayRelatedError} has fired on this
* model key and the caller has cached the degrade in
* {@link StreamOptions.contentArrayCompatibility}.
*/
function streamOptionsContentArrayCompatibilityOk(model, chatProvider, options) {
	if (options?.supportsContentArray !== void 0) return options.supportsContentArray;
	const key = modelKey(model, chatProvider);
	return options?.contentArrayCompatibility?.get(key) !== false;
}
async function resolveTools(options) {
	return (typeof options?.tools === "function" ? await options.tools() : options?.tools) ?? [];
}
function isAbortError(error) {
	return typeof error === "object" && error !== null && error.name === "AbortError";
}
function createCapturedToolErrorResult(toolName, error) {
	return `Tool call error for "${toolName}": ${errorMessageFromValue(error)}`;
}
function withCapturedToolErrors(tools, capturedToolErrorByCallId) {
	return tools.map((tool) => ({
		...tool,
		execute: async (input, executeOptions) => {
			try {
				return await tool.execute(input, executeOptions);
			} catch (error) {
				if (isAbortError(error)) throw error;
				const result = createCapturedToolErrorResult(tool.function.name, error);
				capturedToolErrorByCallId.set(executeOptions.toolCallId, result);
				return result;
			}
		}
	}));
}
function resolveCapturedToolErrorEvent(event, capturedToolErrorByCallId) {
	if (typeof event !== "object" || event === null || event.type !== "tool-result" || typeof event.toolCallId !== "string") return event;
	const toolCallId = event.toolCallId;
	const result = capturedToolErrorByCallId.get(toolCallId);
	if (result == null) return event;
	capturedToolErrorByCallId.delete(toolCallId);
	return {
		...event,
		type: "tool-error",
		isError: true,
		result
	};
}
async function streamFrom({ model, chatProvider, messages, options, builtinToolsResolver }) {
	const chatConfig = chatProvider.chat(model);
	const sanitized = sanitizeMessages(messages, streamOptionsContentArrayCompatibilityOk(model, chatProvider, options));
	const supportedTools = streamOptionsToolsCompatibilityOk(model, chatProvider, options);
	const builtinTools = supportedTools ? await (builtinToolsResolver?.(model, chatProvider) ?? Promise.resolve([])) : [];
	const customTools = supportedTools ? await resolveTools(options) : [];
	const mergedTools = supportedTools ? [...builtinTools, ...customTools] : [];
	const tools = mergedTools.length > 0 ? mergedTools : void 0;
	const capturedToolErrorByCallId = /* @__PURE__ */ new Map();
	const streamTools = options?.captureToolErrors && tools != null ? withCapturedToolErrors(tools, capturedToolErrorByCallId) : tools;
	return new Promise((resolve, reject) => {
		let settled = false;
		const resolveOnce = () => {
			if (settled) return;
			settled = true;
			resolve();
		};
		const rejectOnce = (error) => {
			if (settled) return;
			settled = true;
			reject(error);
		};
		const onEvent = async (event) => {
			try {
				const streamEvent = resolveCapturedToolErrorEvent(event, capturedToolErrorByCallId);
				await options?.onStreamEvent?.(streamEvent);
				if (event && event.type === "finish") {
					const finishReason = event.finishReason;
					if (!(finishReason === "tool_calls" || finishReason === "tool-calls") || !options?.waitForTools) resolveOnce();
				} else if (event && event.type === "error") rejectOnce(event.error ?? /* @__PURE__ */ new Error("Stream error"));
			} catch (error) {
				rejectOnce(error);
			}
		};
		try {
			const streamResult = streamText({
				...chatConfig,
				abortSignal: options?.abortSignal,
				messages: sanitized,
				headers: options?.headers,
				stopWhen: stepCountAtLeast(10),
				tools: streamTools,
				onEvent
			});
			streamResult.steps.then(resolveOnce).catch((error) => {
				rejectOnce(error);
				console.error("Stream steps error:", error);
			});
			streamResult.messages.catch((error) => console.error("Stream messages error:", error));
			streamResult.usage.catch((error) => console.error("Stream usage error:", error));
			streamResult.totalUsage.catch((error) => console.error("Stream totalUsage error:", error));
		} catch (error) {
			rejectOnce(error);
		}
	});
}
const TOOLS_RELATED_ERROR_PATTERNS = [
	/does not support tools/i,
	/no endpoints found that support tool use/i,
	/invalid schema for function/i,
	/invalid.?function.?parameters/i,
	/functions are not supported/i,
	/unrecognized request argument.+tools/i,
	/tool use with function calling is unsupported/i,
	/tool_use_failed/i,
	/does not support function.?calling/i,
	/tools?\s+(is|are)\s+not\s+supported/i
];
function isToolRelatedError(error) {
	const message = String(error);
	return TOOLS_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
const CONTENT_ARRAY_RELATED_ERROR_PATTERNS = [/messages\[\d+\][^"]*invalid type:\s*sequence,\s*expected\s+a\s+string/i, /messages\.\d+\.content[^"]*(?:expected|should be).*string/i];
/**
* Whether the given error indicates the provider rejected content-part arrays
* and the caller should auto-degrade to string-only `content` for this model.
*
* Use when:
* - Catching errors thrown by {@link streamFrom} so the chat store can flip
*   `contentArrayCompatibility` for the failing model key.
*
* Expects:
* - `error` may be an Error instance, a thrown SDK response object, a string,
*   or anything else; we coerce via `String(error)` and pattern-match.
*
* Returns:
* - `true` when the message matches a known "content array unsupported" wire
*   format from an OpenAI-compatible gateway, otherwise `false`.
*/
function isContentArrayRelatedError(error) {
	const message = String(error);
	return CONTENT_ARRAY_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
//#endregion
//#region src/session/merge-loaded-session-messages.ts
function extractMessageContent(message) {
	if (typeof message.content === "string") return message.content;
	if (Array.isArray(message.content)) return message.content.map((part) => {
		if (typeof part === "string") return part;
		if (part && typeof part === "object" && "text" in part) return String(part.text ?? "");
		return "";
	}).join("");
	return "";
}
function getMessageFingerprint(message) {
	return [
		message.id ?? "",
		message.role,
		message.createdAt ?? "",
		extractMessageContent(message)
	].join("");
}
function mergeLoadedSessionMessages(storedMessages, currentMessages) {
	if (currentMessages.length === 0) return storedMessages;
	const currentNonSystemMessages = currentMessages.filter((message, index) => index !== 0 || message.role !== "system");
	if (currentNonSystemMessages.length === 0) return storedMessages;
	const seen = new Set(storedMessages.map(getMessageFingerprint));
	const extraMessages = currentNonSystemMessages.filter((message) => {
		const fingerprint = getMessageFingerprint(message);
		if (seen.has(fingerprint)) return false;
		seen.add(fingerprint);
		return true;
	});
	if (extraMessages.length === 0) return storedMessages;
	const systemMessage = storedMessages[0]?.role === "system" ? storedMessages[0] : currentMessages[0]?.role === "system" ? currentMessages[0] : void 0;
	if (storedMessages.length === 0 && systemMessage) return [systemMessage, ...extraMessages];
	return [...storedMessages, ...extraMessages];
}
//#endregion
export { buildContextPromptMessage, categorizeResponse, createChatHooks, createChatOrchestratorRuntime, createContextRegistry, createStreamingCategorizer, formatContextPromptText, formatTimePrefix, isContentArrayRelatedError, isToolRelatedError, mergeLoadedSessionMessages, modelKey, sanitizeMessages, streamFrom, streamOptionsContentArrayCompatibilityOk, streamOptionsToolsCompatibilityOk, useLlmmarkerParser };
